import { useState, useRef, useEffect, useCallback } from 'react';
import api from '../services/api';

export interface AIChatMessage {
  type: 'bot' | 'user';
  text: string;
}

export function useAIChat() {
  const [messages, setMessages] = useState<AIChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [restantes, setRestantes] = useState<number | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef<AIChatMessage[]>(messages);
  const isTypingRef = useRef(false);

  // Mantém refs sincronizados para evitar stale closures no sendMessage
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  useEffect(() => { isTypingRef.current = isTyping; }, [isTyping]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const buildHistorico = useCallback((msgs: AIChatMessage[]) => {
    const historico: Array<{ role: string; content: string }> = [];
    const recent = msgs.slice(-10);
    for (const m of recent) {
      const role = m.type === 'user' ? 'user' : 'assistant';
      // Concatena mensagens consecutivas do mesmo role em vez de descartar
      if (historico.length > 0 && historico[historico.length - 1].role === role) {
        historico[historico.length - 1].content += '\n' + m.text;
        continue;
      }
      historico.push({ role, content: m.text });
    }
    return historico;
  }, []);

  const sendMessage = useCallback(async (texto: string, prefixo?: string) => {
    if (!texto.trim() || isTypingRef.current) return;

    const userMsg: AIChatMessage = { type: 'user', text: texto };
    setMessages(prev => [...prev, userMsg]);
    setIsTyping(true);

    try {
      // Inclui a mensagem atual no histórico para não perder contexto
      const historico = buildHistorico([...messagesRef.current, userMsg]);
      const mensagem = prefixo ? `${prefixo}\n\n${texto}` : texto;

      const response = await api.post('/Assistente/Chat', {
        mensagem,
        historico,
      });

      setMessages(prev => [...prev, { type: 'bot', text: response.data.resposta }]);
      if (response.data.restantes !== undefined) setRestantes(response.data.restantes);
    } catch (err) {
      console.error('Erro ao chamar assistente IA:', err);
      setMessages(prev => [...prev, {
        type: 'bot',
        text: 'Não consegui processar sua pergunta. Tente novamente ou entre em contato pelo **WhatsApp** no menu **Suporte**.',
      }]);
    } finally {
      setIsTyping(false);
    }
  }, [buildHistorico]);

  return { messages, setMessages, isTyping, restantes, endRef, sendMessage };
}
